import React, { useEffect, useState } from "react";
import { Modal, Card, SkeletonLoader, TextField, Text } from "@merge-api/merge-javascript-shared";
import { Sparkles } from "lucide-react";

import clsx from "clsx";
import AdvancedFieldMappingSelector from "./AdvancedFieldMappingSelector";
import FieldMappingModalTitle from "../../../components/FieldMappingModalTitle";
import CancelAndNextFooter from "../../../../../../../../common/CancelAndNextFooter";
import useAppContext from "../../../../../../../../context/useAppContext";
import AIGeneratedFieldMappingInput from "./AIGeneratedFieldMappingInput";
import { LinkedAccount } from "../../../../../../../../../models/Entities";
import { FieldMappingSwitchValue } from "./FieldMappingModalSwitch";
import FieldMappingModalSwitch from "./FieldMappingModalSwitch";
import { getExpressionLabel } from "../../../../utils/expressionUtils";
import useEditJsonAdvancedMappingExpressionModal from "./useEditJsonAdvancedMappingExpressionModal";

type EditJsonAdvancedMappingExpressionModalProps = {
  jsonData: string;
  remoteFieldDisplayName: string;
  onModalClose: () => void;
  onUpdate: (jsonAdvancedMappingExpression: string) => void;
  currentJsonAdvancedMappingExpression: string | null;
  organizationTargetField?: string;
  linkedAccount?: LinkedAccount;
};
const EditJsonAdvancedMappingExpressionModal = ({
  jsonData,
  currentJsonAdvancedMappingExpression,
  remoteFieldDisplayName,
  organizationTargetField,
  onModalClose,
  onUpdate,
  linkedAccount,
}: EditJsonAdvancedMappingExpressionModalProps) => {
  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user?.is_jmes_path_deprecation_dashboard_enabled ?? false;

  const {
    jsonAdvancedMappingExpression,
    jsonAdvancedMappingExpressionResult,
    inputError,
    errorMessage,
    handleJsonAdvancedMappingExpressionInput,
  } = useEditJsonAdvancedMappingExpressionModal({
    currentJsonAdvancedMappingExpression,
    jsonData,
    isJmesPathDeprecationEnabled,
  });

  const [switchValue, setSwitchValue] = useState<string>(
    currentJsonAdvancedMappingExpression
      ? FieldMappingSwitchValue.Manual
      : FieldMappingSwitchValue.Generated,
  );
  const [hasSubmittedAIQuery, setHasSubmittedAIQuery] = useState<boolean>(false);
  const [isAILoading, setIsAILoading] = useState<boolean>(false);
  // Local state for input to prevent cursor jumping during async evaluation
  const [localInputValue, setLocalInputValue] = useState<string>(
    currentJsonAdvancedMappingExpression ?? "",
  );
  // Persist the AI prompt across mode switches
  const [aiPromptValue, setAiPromptValue] = useState<string>("");

  // Sync local input when hook value changes externally (e.g., from AI generation)
  useEffect(() => {
    if (jsonAdvancedMappingExpression !== null) {
      setLocalInputValue(jsonAdvancedMappingExpression);
    }
  }, [jsonAdvancedMappingExpression]);

  return (
    <Modal open onClose={() => {}}>
      <Card className="pt-[18px] pb-6 w-[800px]">
        <FieldMappingModalTitle
          title={`Advanced mapping - ${remoteFieldDisplayName}`}
          onClose={() => {
            onModalClose();
          }}
        />
        <div className="px-6 pt-5 pb-0">
          <div className="mt-3">
            {user.is_ai_advanced_field_mapping_generation_enabled &&
            switchValue === FieldMappingSwitchValue.Generated ? (
              <>Describe the data you want to map.</>
            ) : (
              <>
                Select the field inside the list object you want to map, or specify a JSON query
                below using{" "}
                <a
                  href={
                    isJmesPathDeprecationEnabled
                      ? "https://docs.jsonata.org/overview"
                      : "https://jmespath.org/"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {getExpressionLabel(isJmesPathDeprecationEnabled)}
                </a>
                .
              </>
            )}
          </div>
          <div>
            <AdvancedFieldMappingSelector
              jsonString={JSON.stringify(jsonData)}
              handleJsonAdvancedMappingExpressionInput={(jmesPath: any) => {
                if (
                  !user.is_ai_advanced_field_mapping_generation_enabled ||
                  switchValue == FieldMappingSwitchValue.Manual
                ) {
                  handleJsonAdvancedMappingExpressionInput(jmesPath);
                }
              }}
            />
          </div>
          {user.is_ai_advanced_field_mapping_generation_enabled &&
            switchValue === FieldMappingSwitchValue.Generated && (
              <AIGeneratedFieldMappingInput
                jsonData={jsonData}
                handleJsonAdvancedMappingExpressionInput={handleJsonAdvancedMappingExpressionInput}
                linkedAccount={linkedAccount}
                remoteField={remoteFieldDisplayName}
                onEnter={() => {
                  setHasSubmittedAIQuery(true);
                }}
                isLoading={isAILoading}
                setIsLoading={setIsAILoading}
                promptValue={aiPromptValue}
                onPromptChange={setAiPromptValue}
              />
            )}
          {(!user.is_ai_advanced_field_mapping_generation_enabled ||
            switchValue === FieldMappingSwitchValue.Manual ||
            (isAILoading && switchValue === FieldMappingSwitchValue.Generated) ||
            (switchValue === FieldMappingSwitchValue.Generated &&
              (jsonAdvancedMappingExpressionResult ||
                currentJsonAdvancedMappingExpression ||
                (hasSubmittedAIQuery && jsonAdvancedMappingExpression)))) && (
            <div className="mt-3">
              {isAILoading &&
              user.is_ai_advanced_field_mapping_generation_enabled &&
              switchValue === FieldMappingSwitchValue.Generated ? (
                <div className="flex items-start gap-2 rounded-md border border-[#D5DAE1] bg-white px-3 py-2 h-[112px]">
                  <Sparkles size={16} className="text-gray-40 flex-shrink-0" />
                  <SkeletonLoader height={16} borderRadius={4} fullWidth />
                </div>
              ) : (
                <TextField
                  placeholder="Example query: array[?type=='value'].result"
                  multiline
                  rows={4}
                  inputClassName="resize-y"
                  value={localInputValue}
                  error={inputError}
                  errorText={errorMessage ?? undefined}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Update local state immediately to preserve cursor position
                    setLocalInputValue(newValue);
                    // Then trigger async evaluation
                    handleJsonAdvancedMappingExpressionInput(newValue);
                  }}
                />
              )}
            </div>
          )}
          <div className="mt-3 bg-gray-0 py-2.5 px-3 rounded-md max-h-[120px] overflow-auto">
            <span className="font-semibold pr-1">Result: </span>
            <span className="break-all">{jsonAdvancedMappingExpressionResult}</span>
          </div>
        </div>
        <div
          className={clsx(
            "",
            (organizationTargetField || user.is_ai_advanced_field_mapping_generation_enabled) &&
              "flex items-center justify-between",
          )}
        >
          <div className="ml-6 mt-6 gap-1">
            {user.is_ai_advanced_field_mapping_generation_enabled && (
              <FieldMappingModalSwitch
                switchValue={switchValue}
                setSwitchValue={(value) => {
                  setSwitchValue(value);
                }}
              />
            )}
            {organizationTargetField && (
              <Text variant="md" className="text-left ml-6 pt-6">
                {organizationTargetField}
              </Text>
            )}
          </div>
          <CancelAndNextFooter
            className="mt-6 px-6"
            onCancel={() => {
              onModalClose();
            }}
            isNextDisabled={!localInputValue.trim() || inputError}
            buttonText="Save"
            onNext={() => {
              onUpdate(jsonAdvancedMappingExpression!);
            }}
          />
        </div>
      </Card>
    </Modal>
  );
};
export default EditJsonAdvancedMappingExpressionModal;
